import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext.js';
import { LanguageSwitcher } from '../shared/components/LanguageSwitcher.js';
import { useBrand } from '../shared/components/BrandContext.js';
import { KordaroLogo } from '../shared/components/KordaroLogo.js';

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const brand = useBrand();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-2">
          <LanguageSwitcher />
        </div>
        <div className="text-center mb-8">
          {brand.isWhitelabel ? (
            <>
              {brand.logoUrl ? (
                <img src={brand.logoUrl} alt={brand.naam} className="mx-auto h-16 max-w-[220px] object-contain mb-4" />
              ) : (
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-secondary mb-4">
                  <span className="text-2xl font-semibold text-white">{brand.naam.slice(0, 2).toUpperCase()}</span>
                </div>
              )}
              <h1 className="text-2xl font-semibold text-gray-900">{brand.naam}</h1>
            </>
          ) : (
            <h1 className="flex justify-center mb-2">
              <KordaroLogo width={200} />
            </h1>
          )}
          <p className="text-gray-500 mt-1">{t('auth.tagline')}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border border-card-border rounded-[14px] p-8 shadow-sm">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold"
              placeholder={t('auth.emailPlaceholder')}
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.password')}</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2 pr-16 border border-gray-500 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-gold hover:bg-gold-dark text-white font-medium rounded-lg transition-colors disabled:bg-gray-300 disabled:text-gray-700 disabled:cursor-not-allowed"
          >
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </button>

          <p className="text-center text-sm text-gray-500 mt-4">
            <a href="/forgot-password" className="text-gold font-medium hover:underline">{t('auth.forgotPassword')}</a>
          </p>
        </form>
      </div>
    </div>
  );
}
